import {
 Box,
 CircularProgress,
 SxProps,
 Tab,
 Tabs,
 Theme,
} from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import { CommonState } from '../../../redux/_common/commonReducers';
import { getBaseDomain } from '../../../redux/pageDomain';
import { RootState } from '../../../redux/store';

const defaultTabSx = {
 '& .Mui-selected': {
  backgroundColor: (theme: Theme) => theme.palette.kemek.white,
 },
 '& .MuiButtonBase-root.MuiTab-root': {
  pt: '12px',
  pb: '12px',
  fontSize: '14px',
  fontWeight: 400,
  '&[aria-selected="false"]': {
   color: (theme: Theme) => theme.palette.kemek.darkGray,
  },
  '&[aria-selected="true"]': {
   color: (theme: Theme) => theme.palette.kemek.blue,
  },
 },
 '& .MuiTabs-indicator': {
  height: '1px',
 },
};

const defaultPanelSx = {
 p: 0,
 height: '100%',
};

interface TabPanelProps {
 item: TabItemProps;
 children: ReactNode;
 index: number;
 value: number;
}
interface TabItemProps {
 key: string;
 content: ReactNode;
 disabled?: boolean;
 count?: number;
}
interface EditTabsProps {
 tabs: TabItemProps[];
 defaultTab?: number;
 variant?: 'fullWidth' | 'standard' | 'scrollable';
 ariaLabel?: string;
 sx?: SxProps<Theme>;
 tabSx?: SxProps<Theme>;
 panelSx?: SxProps<Theme>;
 onChange?: (newValue: number) => void;
}

function a11yProps(index: number) {
 return {
  id: `edittabs-tab-${index}`,
  'aria-controls': `edittabs-tabpanel-${index}`,
 };
}

function EditTabPanel(props: TabPanelProps) {
 const { pageName } = usePage();
 const { children, value, index, item, ...other } = props;

 const selector = useSelector(
  (state: RootState) => state[getBaseDomain(pageName)],
 ) as CommonState;
 const { loading, structure } = selector;

 const isMoreThanOneTab = useMemo(
  () => structure?.filter((field) => field.Type === 'array').length > 0,
  [structure, item],
 );

 return (
  <div
   style={{
    height: '100%',
   }}
   role="tabpanel"
   hidden={value !== index}
   id={`edittabs-tabpanel-${index}`}
   aria-labelledby={`edittabs-tab-${index}`}
   {...other}
  >
   {value === index && (
    // magic
    <Box
     sx={{
      p: isMoreThanOneTab ? '20px 24px' : '0px 24px 20px 24px',
      overflowY: 'auto',
      height:
       item.key !== 'data'
        ? `calc(100% - ${isMoreThanOneTab ? '48px' : '0px'})`
        : '100%',
     }}
    >
     {loading?.getItem ? (
      <Box
       sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100px',
       }}
      >
       <CircularProgress
        color="primary"
        size={50}
        sx={{ pointerEvents: 'none' }}
       />
      </Box>
     ) : (
      children
     )}
    </Box>
   )}
  </div>
 );
}

const EditTabs = ({
 tabs,
 defaultTab = 0,
 variant = 'fullWidth',
 ariaLabel = 'Tabs',
 sx,
 tabSx,
 panelSx,
 onChange,
}: EditTabsProps) => {
 const { t } = useTranslation();
 const [tab, setTab] = useState(defaultTab);

 const handleChange = (event: React.SyntheticEvent, newValue: number) => {
  setTab(newValue);
  onChange?.(newValue);
 };

 return (
  <Box
   sx={{
    height: '100%',
   }}
  >
   {tabs.length > 1 && (
    <Box
     sx={{
      borderBottom: 1,
      borderBottomColor: (theme: Theme) => theme.palette.kemek.lightestGray,
      mt: '-1px',
      ...sx,
     }}
    >
     <Tabs
      variant={variant}
      value={tab}
      onChange={handleChange}
      aria-label={ariaLabel}
      sx={{
       ...defaultTabSx,
       ...tabSx,
      }}
     >
      {tabs.map((tabItem, index) => (
       <Tab
        key={tabItem.key}
        label={
         tabItem.count ? `${tabItem.key} (${tabItem.count})` : tabItem.key
        }
        disabled={tabItem.disabled}
        sx={{
         whiteSpace: 'nowrap',
        }}
        {...a11yProps(index)}
       />
      ))}
     </Tabs>
    </Box>
   )}

   {tabs.map((tabItem, index) => (
    <EditTabPanel key={tabItem.key} item={tabItem} value={tab} index={index}>
     <Box sx={{ ...defaultPanelSx, ...panelSx }}>{tabItem.content}</Box>
    </EditTabPanel>
   ))}
  </Box>
 );
};

export default EditTabs;

